import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, Select, message, Drawer, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, EyeOutlined,
  CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type PowerType } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'

const BASE = '/master/power-types'

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const PowerTypeMaster: React.FC = () => {
  const { powerTypes, deletePowerType, savePowerType, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = useState<PowerType | null>(null)
  const [form]                  = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const activeCount = powerTypes.filter(s => s.status === 'active').length

  const filteredPowerTypes = powerTypes.filter(p => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      p.name.toLowerCase().includes(q) ||
      (p.code || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || p.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
    } else if (mode === 'edit') {
      const rec = powerTypes.find(s => s.id === editId)
      if (rec) form.setFieldsValue(rec)
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, powerTypes])

  const handleDelete = async (id: string) => {
    try {
      await deletePowerType(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let code = values.code
    if (!isEdit) {
      try {
        code = await generateCode('power_types')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a code — check your connection and try again.")
        return
      }
    }
    const payload = { ...values, code, id: isEdit ? editId : genId('PT') }
    setSubmitting(true)
    try {
      await savePowerType(payload, isEdit)
      message.success(isEdit ? 'Power Type updated' : 'Power Type added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'code', width: 180,
      render: (v: string) => (
        <span style={{ ...MONO, fontWeight: 700, fontSize: 14, color: '#1677ff' }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Name', dataIndex: 'name', width: 200,
      render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: 'Description', dataIndex: 'description',
      render: (v: string) => <span style={{ color: '#4A5568', fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: PowerType) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Power Type' : 'Add Power Type'}
        subtitle="Power Type referenced across contract templates and billing"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 640 }}>
          <Form.Item name="code" label="Power Type Code">
            <Input disabled placeholder="Auto-generated on save" style={{ ...MONO, fontWeight: 700 }} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please input name' }]}>
            <Input placeholder="e.g. Solar Power" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of the power type" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Power Type' : 'Add Power Type'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── Full-page Detail View ──────────────────────────────────────
  if (viewRecord) {
    return (
      <MasterFormShell
        title={`View Power Type — ${viewRecord.name}`}
        subtitle="Power Type Details"
        onBack={() => setViewRecord(null)}
        extra={
          <Space>
            <Button type="primary" icon={<EditOutlined />} onClick={() => { goEdit(viewRecord.id); setViewRecord(null); }}>Edit</Button>
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => { handleDelete(viewRecord.id); setViewRecord(null); }}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>Delete</Button>
            </Popconfirm>
          </Space>
        }
      >
        <div style={{ maxWidth: 640 }}>
          <div style={{ marginBottom: 20 }}>
            <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
              {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
          <DetailRow label="Code" value={<span style={MONO}>{viewRecord.code}</span>} />
          <DetailRow label="Name" value={<strong>{viewRecord.name}</strong>} />
          <DetailRow label="Description" value={viewRecord.description} />
          
          <div style={{ display: 'flex', gap: 8, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={() => setViewRecord(null)}>Back to List</Button>
          </div>
        </div>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={5} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SettingOutlined style={{ color: '#001A4D' }} /> Type of Power
          </h2>
          <p className="page-subtitle">Configure Power Types (e.g. Third Party, Group Captive) referenced across contract templates and billing</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Power Type</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Power Types', value: powerTypes.length, icon: <SettingOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active Types', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name, code or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredPowerTypes}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>
    </div>
  )
}

export default PowerTypeMaster
